import { ApiServiceWithSettings, Audience, Authentication } from '../../common'
import { AxiosPromise } from 'axios'
import qs from 'qs'
import { Pagination, Tasks } from '@one/types'

export class BackofficeTasksApi extends ApiServiceWithSettings {
  getTasks(
    params: Tasks.Tasks.TaskFilteringQuery
  ): AxiosPromise<Pagination.Pager<Tasks.Tasks.Task>> {
    return this.get('/tasks', {
      params: {
        q: params.q,
        categories: params.categories,
        statuses: params.statuses,
        assignedToUser: params.assignedToUser,
        createdByUser: params.createdByUser,
        watchers: params.watchers,
        clients: params.clients,
        offers: params.offers,
        salesOrders: params.salesOrders,
        warehouseDocuments: params.warehouseDocuments,
        salesDocuments: params.salesDocuments,
        url: params.url,
        startDate: params.startDate,
        dueDate: params.dueDate,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        onlyUnread: params.onlyUnread,
        onlyImportant: params.onlyImportant
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  getTask(id: number): AxiosPromise<Tasks.Tasks.Task> {
    return this.get(`/tasks/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createTask(task: Tasks.Tasks.CreateTaskDto): AxiosPromise<Tasks.Tasks.Task> {
    return this.post('/tasks', task, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateTask(
    taskId: number,
    task: Tasks.Tasks.UpdateTaskDto
  ): AxiosPromise<void> {
    return this.patch(`/tasks/${taskId}`, task, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteTask(id: number): AxiosPromise<void> {
    return this.delete(`/tasks/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getUnreadTasksList(): AxiosPromise<
    Array<Pick<Tasks.Tasks.UnreadTask, 'taskId' | 'createDate'>>
  > {
    return this.get('/tasks/unread-list', {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  setTaskAsRead(taskId: number): AxiosPromise<void> {
    return this.post(`/tasks/${taskId}/read`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getStatuses(): AxiosPromise<Array<Tasks.Statuses.Status>> {
    return this.get('/statuses', {
      authentication: Authentication.PUBLIC,
      aud: Audience.BACKOFFICE
    })
  }

  getCategories(
    pageNumber: number,
    pageSize: number,
    withDeactivated: boolean
  ): AxiosPromise<Pagination.Pager<Tasks.Categories.Category>> {
    return this.get('/categories', {
      params: {
        pageNumber,
        pageSize,
        ...(withDeactivated ? { withDeactivated } : {})
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategory(id: number): AxiosPromise<Tasks.Categories.Category> {
    return this.get(`/categories/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createCategory(
    category: Tasks.Categories.CreateCategoryDto
  ): AxiosPromise<Tasks.Categories.Category> {
    return this.post('/categories', category, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateCategory(
    categoryId: string,
    category: Tasks.Categories.UpdateCategoryDto
  ): AxiosPromise<Tasks.Categories.Category> {
    return this.patch(`/categories/${categoryId}`, category, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  removeCategory(categoryId: number): AxiosPromise<void> {
    return this.delete(`/categories/${categoryId}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  activateCategory(categoryId: number): AxiosPromise<void> {
    return this.post(`/categories/${categoryId}/activate`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deactivateCategory(categoryId: number): AxiosPromise<void> {
    return this.post(`/categories/${categoryId}/deactivate`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategoriesSuggestions(
    query: string
  ): AxiosPromise<Array<Tasks.Categories.Category>> {
    return this.get('/categories/suggest', {
      params: {
        userQuery: query
      },
      cache: {
        ttl: 60 * 1000,
        tags: ['categories', 'auth-optional']
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllImportantTasks(): AxiosPromise<
    Array<Pick<Tasks.Tasks.ImportantTask, 'taskId' | 'createDate'>>
  > {
    return this.get('/tasks/important-list', {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  setTaskAsNotImportant(taskId: number): AxiosPromise<void> {
    return this.post(`/tasks/${taskId}/unset-important`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  setTaskAsImportant(taskId: number): AxiosPromise<void> {
    return this.post(`/tasks/${taskId}/set-important`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSalesRepSchedules(
    pageNumber: number = 1,
    pageSize: number = 10
  ): AxiosPromise<Tasks.SalesRep.PagerSalesRepScheduleListView> {
    return this.get('/sales-rep-schedule/', {
      params: { pageNumber, pageSize },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSalesRepSchedule(
    id: string
  ): AxiosPromise<Tasks.SalesRep.SalesRepScheduleView> {
    return this.get(`/sales-rep-schedule/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createSalesRepSchedule(): AxiosPromise<Tasks.SalesRep.SalesRepScheduleView> {
    return this.post('/sales-rep-schedule/', null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateSalesRepSchedule(
    id: string,
    commands: Tasks.SalesRep.Commands
  ): AxiosPromise<Tasks.SalesRep.SalesRepScheduleView> {
    return this.post(`/sales-rep-schedule/${id}`, commands, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createSalesRepScheduleTasks(
    id: string,
    command: Tasks.SalesRep.GenerateTasksCommand
  ): AxiosPromise<void> {
    return this.post(`/sales-rep-schedule/${id}/create-tasks`, command, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSalesRepScheduleCalendar(
    id: string,
    year: number,
    month: number
  ): AxiosPromise<Tasks.SalesRep.CalendarResponse> {
    return this.get(`/sales-rep-schedule/${id}/calendar/${year}/${month}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  stopWorkLog(taskId: number): AxiosPromise<Tasks.Tasks.WorkLogDto> {
    return this.post(`/tasks/${taskId}/worklog/stop`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getActiveWorkLog(): AxiosPromise<Tasks.Tasks.Task> {
    return this.get(`/worklog/active`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  startWorkLog(taskId: number): AxiosPromise<Tasks.Tasks.WorkLogDto> {
    return this.post(`/tasks/${taskId}/worklog/start`, null, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }
}
